import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { StateStore } from "../../services/state/state.store";
import { AcademicLabels } from "./academic.labels";
import { AcademicNavItem, AcademicNavItems } from "./academic.nav";
import { AcademicSidebarStore } from "./academic.sidebar.store";

@TypeManagerDecorator("s25-ng-academic-sidebar")
@Component({
    selector: "s25-ng-academic-sidebar",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, S25RouterLinkDirective],
    host: { class: "academic-sidebar" },
    template: `
        <div class="sidebar-backdrop" [class.visible]="sidebarStore.isOpen()" (click)="sidebarStore.close()"></div>
        <aside id="academic-sidebar" class="sidebar" [class.open]="sidebarStore.isOpen()">
            <nav [attr.aria-label]="labels.PRODUCT">
                <div class="academic-sidebar__section-label">{{ labels.PRODUCT }}</div>
                @for (item of navItems; track item.stateName) {
                    <a
                        s25-ng-router-link
                        [stateName]="item.stateName"
                        class="academic-sidebar__link"
                        [class.academic-sidebar__link--current]="isCurrent(item)"
                        [attr.aria-current]="isCurrent(item) ? 'page' : null"
                        (click)="onNavClick()"
                    >
                        <s25-ng-icon [type]="item.icon" [size]="20" [ariaLabel]="false" />
                        <span>{{ item.label }}</span>
                    </a>
                }
            </nav>
        </aside>
    `,
    styles: `
        :host {
            display: block;
        }

        .sidebar-backdrop {
            display: none;
        }

        .sidebar {
            width: 0;
            background: var(--color--surface-bg);
            border-radius: 16px;
            padding: 0;
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 10%, transparent);
            overflow: hidden;
            transition:
                width var(--transition-medium) ease,
                padding var(--transition-medium) ease;
        }

        .sidebar.open {
            width: 280px;
            padding: 24px 16px;
        }

        @media (max-width: 930px) {
            :host {
                position: fixed;
                top: 0;
                left: 0;
                z-index: 1000;
                pointer-events: none;
            }

            .sidebar-backdrop {
                display: block;
                position: fixed;
                inset: 0;
                background: color-mix(in srgb, var(--color--text-strong) 40%, transparent);
                opacity: 0;
                visibility: hidden;
                transition:
                    opacity var(--transition-medium) ease,
                    visibility var(--transition-medium) ease;
                pointer-events: none;
            }

            .sidebar-backdrop.visible {
                opacity: 1;
                visibility: visible;
                pointer-events: auto;
            }

            .sidebar {
                position: fixed;
                top: 0;
                left: 0;
                height: 100vh;
                width: 280px;
                padding: 24px 16px;
                border-radius: 0 16px 16px 0;
                transform: translateX(-100%);
                transition: transform var(--transition-medium) ease;
                overflow-y: auto;
                z-index: 1001;
                pointer-events: auto;
            }

            .sidebar.open {
                transform: translateX(0);
            }
        }

        nav {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .academic-sidebar__section-label {
            padding: 0 14px 8px;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .academic-sidebar__link {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 14px;
            border-radius: 8px;
            color: var(--color--text-muted);
            text-decoration: none;
            transition: background-color var(--transition-medium) ease;
        }

        .academic-sidebar__link:hover {
            background: var(--color--surface-bg-hover);
            color: var(--color--text-strong);
            text-decoration: none;
        }

        .academic-sidebar__link--current {
            background: var(--color--primary-flat-bg);
            color: var(--color--link-text);
            font-weight: 600;
        }
    `,
})
export class AcademicSidebarComponent {
    private readonly stateStore = inject(StateStore);
    readonly sidebarStore = inject(AcademicSidebarStore);
    readonly labels = AcademicLabels;
    readonly navItems = AcademicNavItems;

    isCurrent(item: AcademicNavItem): boolean {
        return this.stateStore.currentStateName() === item.stateName;
    }

    onNavClick(): void {
        if (window.innerWidth <= 930) {
            this.sidebarStore.close();
        }
    }
}
